/**
 * Merchant Banner Wizard — cross-window protocol (self-serve MVP, B2).
 *
 * Shared between the store-page overlay (editor bundle: MerchantBannerOverlay)
 * and the app-side wizard (MerchantBannerWizard). Uses its own TNX_MB_* message
 * namespace, deliberately disjoint from the consultant editor's TNX_* vocabulary
 * handled by BottomRibbon — the two flows never share a listener.
 *
 * Unlike the consultant protocol, every message here is exchanged with an
 * explicit origin on BOTH sides: the wizard passes its origin to the overlay via
 * the `tnx_app_origin` URL param, and posts to the popup with the store origin.
 */

import { MOBILE_PREVIEW_WIDTH } from '@/components/visual-editor/deviceViewport'

export type BannerElementType = 'img-src' | 'bg-image' | 'img-child'

/** The merchant-confirmed control banner, as captured on the store page. */
export interface MerchantBannerControl {
  selector: string
  elementType: BannerElementType
  /** Current banner image URL (best effort — null for unresolvable bg-images). */
  imageUrl: string | null
  /** Rendered size at confirmation time, in the popup's viewport. */
  width: number
  height: number
  /**
   * The popup's viewport width at confirmation. Dual-device detection opens one
   * popup per device; this is how the wizard attributes a CONFIRMED message to
   * the right slot instead of trusting which window it opened last.
   */
  viewportWidth?: number
  /** How many elements the selector matches (1 is the safe case). */
  matchCount: number
  isCarousel: boolean
}

export type MerchantBannerUnsupportedReason = 'carousel' | 'not_found' | 'wrong_page'

// ---------------------------------------------------------------------------
// Message types
// ---------------------------------------------------------------------------

/** Overlay (store page) → wizard (app). */
export const MB_MSG = {
  /** Overlay booted; repeated every second until the wizard ACKs. */
  READY: 'TNX_MB_READY',
  /** A candidate is highlighted, waiting for merchant confirmation. */
  CANDIDATE: 'TNX_MB_CANDIDATE',
  /** Merchant confirmed the control. Payload: { control: MerchantBannerControl } */
  CONFIRMED: 'TNX_MB_CONFIRMED',
  /** Typed refusal. Payload: { reason: MerchantBannerUnsupportedReason } */
  UNSUPPORTED: 'TNX_MB_UNSUPPORTED',
  /**
   * Preview step (0.2): the merchant drives the preview from INSIDE the store
   * window, so these three carry their decisions back to the wizard.
   * Payload: { device: MbDevice } — switch the preview to the other screen.
   */
  PREVIEW_DEVICE: 'TNX_MB_PREVIEW_DEVICE',
  /** "Se ve bien" — merchant accepted the preview; wizard advances to recap. */
  PREVIEW_OK: 'TNX_MB_PREVIEW_OK',
  /** "Volver atrás" — merchant wants to change the image; wizard goes back. */
  PREVIEW_BACK: 'TNX_MB_PREVIEW_BACK',
  /**
   * Which screen the store window is ACTUALLY showing, self-classified by the
   * only side that can measure it — an opener can't read a cross-origin
   * window's size. Payload: { device, width }. Sent after a SHOW_SCREEN request
   * and on manual resizes, so a request the browser refused is still visible.
   */
  SCREEN: 'TNX_MB_SCREEN',
} as const

/** Wizard (app) → overlay (store page). */
export const MB_CMD = {
  /** Stops the overlay's READY announcement loop. */
  ACK: 'TNX_MB_ACK',
  /** Apply challenger preview. Payload: { previewUrl: string } */
  PREVIEW: 'TNX_MB_PREVIEW',
  /**
   * Warm challenger images in the STORE window's HTTP cache ahead of preview.
   * Payload: { urls: string[] }. Needed because Chrome partitions HTTP cache
   * by top-level site — the app window's upload thumbnails don't warm the
   * popup's cache, so an unwarmed first preview waits on a cold CDN fetch.
   */
  WARM: 'TNX_MB_WARM',
  /** Restore the original banner. */
  REVERT: 'TNX_MB_REVERT',
  /** Re-enter tap-to-select mode. */
  RESELECT: 'TNX_MB_RESELECT',
  /**
   * Re-adopt a previously confirmed control (popup was reopened mid-wizard).
   * Payload: { selector: string }. Overlay re-validates and replies CONFIRMED.
   */
  ADOPT: 'TNX_MB_ADOPT',
  /**
   * Switch the overlay into preview mode — the floating "Así se va a ver tu
   * banner nuevo" card with its device toggle and accept/back actions.
   * Payload: { device: MbDevice }. Sent right after a PREVIEW.
   */
  PREVIEW_MODE: 'TNX_MB_PREVIEW_MODE',
  /**
   * Ask the store window to show a device's screen — ONE window serves both
   * ("la misma ventana abierta con el viewport de celular"). This is a REQUEST
   * of intent, not a layout instruction: the overlay decides how to realize a
   * screen (today by resizing itself, which only it can do — an opener cannot
   * resize a cross-origin popup) and answers with MB_MSG.SCREEN.
   *
   * Payload: { device, redetect?, adoptSelector?, previewUrl? }. Because each
   * breakpoint has its OWN banner element, the caller says what should happen
   * once the new screen settles: re-detect from scratch (step 1) or re-adopt a
   * known control and swap in an image (preview).
   */
  SHOW_SCREEN: 'TNX_MB_SHOW_SCREEN',
} as const

// ---------------------------------------------------------------------------
// Activation params (store-page side)
// ---------------------------------------------------------------------------

/** URL param that switches the editor bundle into merchant-banner mode. */
export const MB_MODE_PARAM = 'tnx_merchant_banner'
/** URL param carrying the app origin for postMessage targeting/validation. */
export const MB_APP_ORIGIN_PARAM = 'tnx_app_origin'

/**
 * Merchant mode detection with per-tab persistence: the URL param survives
 * storefront navigation only for `tnx_visual_editor` (tnx-tracking's link
 * interceptor), so we persist our flag in sessionStorage. sessionStorage is
 * per-tab, so a merchant's flag can never leak into a consultant's editor
 * popup (always a fresh window ⇒ fresh sessionStorage).
 */
export function isMerchantBannerMode(): boolean {
  try {
    const params = new URLSearchParams(window.location.search)
    if (params.get(MB_MODE_PARAM) === '1') {
      sessionStorage.setItem(MB_MODE_PARAM, '1')
      const appOrigin = params.get(MB_APP_ORIGIN_PARAM)
      if (appOrigin) sessionStorage.setItem(MB_APP_ORIGIN_PARAM, appOrigin)
      return true
    }
    return sessionStorage.getItem(MB_MODE_PARAM) === '1'
  } catch {
    return false
  }
}

/** The wizard's origin, from URL param or the per-tab persisted copy. */
export function getMerchantAppOrigin(): string | null {
  try {
    const params = new URLSearchParams(window.location.search)
    return params.get(MB_APP_ORIGIN_PARAM) || sessionStorage.getItem(MB_APP_ORIGIN_PARAM)
  } catch {
    return null
  }
}

// ---------------------------------------------------------------------------
// Dual-device detection
// ---------------------------------------------------------------------------

export type MbDevice = 'desktop' | 'mobile'

/**
 * Size for the INITIAL window.open. Once open, screens are switched by the
 * overlay resizing itself (visual-editor/deviceViewport) — the same mechanism
 * the consultant editor's "Switch to Mobile" uses.
 *
 * Mobile is 375 to match that editor behavior (BottomRibbon), and it must land
 * under the runtime breakpoint so the storefront serves its mobile layout:
 * tnx-core/deviceClass and the bundle's tnxDeviceClass both split at 767/768px.
 */
export const MB_POPUP_SIZE: Record<MbDevice, { width: number; height: number }> = {
  desktop: { width: 1280, height: 850 },
  mobile: { width: MOBILE_PREVIEW_WIDTH, height: 850 },
}

/** Same 767/768 split the runtime uses, applied to a reported viewport width. */
export function deviceForViewport(width: number | undefined): MbDevice | null {
  if (typeof width !== 'number' || !Number.isFinite(width)) return null
  return width <= 767 ? 'mobile' : 'desktop'
}

// ---------------------------------------------------------------------------
// Carousel policy (spec §7.2 — resolved: supported via slide pinning)
// ---------------------------------------------------------------------------

/**
 * 'first_slide': target the confirmed slide's image, pinned to its logical
 * slide index (pinnedCarouselSelector) so rotation and loop-mode clones don't
 * move the target. 'refuse' kept as an escape hatch.
 */
export const MB_CAROUSEL_POLICY: 'refuse' | 'first_slide' = 'first_slide'

// ---------------------------------------------------------------------------
// Experiment config assembly (wizard side)
// ---------------------------------------------------------------------------

export interface MbUploadedAsset {
  asset_id: string
  s3_key: string
  previewUrl: string
}

export interface MbUploads {
  mobile?: MbUploadedAsset
  desktop?: MbUploadedAsset
}

/** Per-device confirmed controls (dual detection — desktop AND mobile popup). */
export interface MbControls {
  desktop: MerchantBannerControl
  mobile: MerchantBannerControl
}

const isImgControl = (c: MerchantBannerControl) =>
  c.elementType === 'img-src' || c.elementType === 'img-child'

/**
 * Stable id stamped on an inserted challenger wrapper (`data-tnx-insert-id`), so the CTR events can
 * target it as well as the control element. See §4.5 of OPTIMIZATIONS-SPEC.md — without this, every
 * `bg-image` arm measures zero.
 */
export const bannerInsertId = (slotId: string, arm: string, device: MbDevice) =>
  `tnx-banner-${slotId}-${arm}-${device}`

/**
 * The selector list the click + visibility custom events must use: the per-device control elements
 * plus every inserted challenger wrapper. `closest()` and `querySelectorAll()` both take lists, so
 * one event pair measures "the banner" on every arm and every screen.
 */
export function bannerEventSelectors(
  controls: MbControls,
  arms: Array<{ arm: string; slotId: string }>
): string {
  const parts = new Set<string>()
  for (const device of ['desktop', 'mobile'] as const) {
    const control = controls[device]
    if (!control) continue
    parts.add(control.selector)
    // Only a bg-image control produces an inserted element; an img control is mutated in place and
    // is already covered by its own selector.
    if (!isImgControl(control)) {
      for (const { arm, slotId } of arms) {
        parts.add(`[data-tnx-insert-id="${bannerInsertId(slotId, arm, device)}"]`)
      }
    }
  }
  return Array.from(parts).join(', ')
}

function controlMarker(c: MerchantBannerControl) {
  return {
    selector: c.selector,
    element_type: c.elementType,
    control_image: c.imageUrl,
    control_size: { width: c.width, height: c.height },
  }
}

/**
 * Build the variant config for the self-serve banner experiment, from the two
 * device-confirmed controls. Mirrors the shapes TemplatesTab.buildConfig()
 * emits for the consultant hero_banner template (in_place → elements_to_swap;
 * bg-image → hide + device-gated inserts), so the backend promotion path
 * (experiments-handler collects s3_key/asset_id fields) works unchanged.
 *
 * Two topologies:
 *  - SAME element on both devices (srcset/picture themes): one swap entry with
 *    both assets — tnx-core builds the srcset and the browser picks per device.
 *  - DIFFERENT elements per device: one device-gated entry per control, each
 *    carrying only its own asset (applySwap/applyFeatureToggle/applyInsertions
 *    all honor `device`).
 */
export function buildSelfServeBannerConfig(
  controls: MbControls,
  uploads: MbUploads,
  options: { arm?: string; slotId?: string } = {}
) {
  const SOURCE = 'template:self_serve_banner'
  const urlPattern = '/' // homepage only (urlPatterns.ts TIENDANUBE_PATTERNS.homepage)
  // A/B keeps the historical 'treatment'; a slot passes variant_b/variant_c/…
  const arm = options.arm || 'treatment'
  const slotId = options.slotId || 'ab'

  const elements_to_hide: any[] = []
  const elements_to_insert: any[] = []
  const elements_to_swap: any[] = []

  const sameElement =
    controls.desktop.selector === controls.mobile.selector &&
    isImgControl(controls.desktop) &&
    isImgControl(controls.mobile)

  if (sameElement) {
    // In-place: mutate the existing <img> (mobile+desktop combine into srcset
    // at runtime). Layout-preserving, no flicker.
    elements_to_swap.push({
      selector: controls.desktop.selector,
      urlPattern,
      variant: arm,
      asset_id_desktop: uploads.desktop?.asset_id,
      s3_key_desktop: uploads.desktop?.s3_key,
      asset_id_mobile: uploads.mobile?.asset_id,
      s3_key_mobile: uploads.mobile?.s3_key,
      source: SOURCE,
    })
  } else {
    for (const device of ['desktop', 'mobile'] as const) {
      const control = controls[device]
      const asset = uploads[device]
      if (!asset) continue
      if (isImgControl(control)) {
        elements_to_swap.push({
          selector: control.selector,
          urlPattern,
          variant: arm,
          device,
          [`asset_id_${device}`]: asset.asset_id,
          [`s3_key_${device}`]: asset.s3_key,
          source: SOURCE,
        })
      } else {
        // bg-image: hide the control block, insert the challenger image as a
        // sibling — both gated to this device's viewport.
        elements_to_hide.push({
          selector: control.selector,
          urlPattern,
          hideIn: arm,
          device,
          source: SOURCE,
          description: `Banner principal — bloque original (${device})`,
        })
        elements_to_insert.push({
          selector: control.selector,
          urlPattern,
          position: 'beforebegin' as const,
          asset_id: asset.asset_id,
          s3_key: asset.s3_key,
          variant: arm,
          image_type: `${device}_image`,
          device,
          // A block wrapper, not the default inline span: this stands in for a hidden block-level
          // hero, and the visibility event needs a reliable intersection box to observe.
          wrapperType: 'block' as const,
          // MEASURABILITY (OPTIMIZATIONS-SPEC §4.5). The challenger lands in a SIBLING wrapper of a
          // now-hidden control, so the click event's `closest(controlSelector)` and the visibility
          // observer — both bound to the control — can never match it: zero clicks and zero views
          // on this arm, structurally, whatever the creative is worth. tnx-core stamps this id on
          // the wrapper (`data-tnx-insert-id`), so a deterministic value lets the events target the
          // inserted element too. Without it an A/B walks a bg-image store to a false
          // control_better, and a slot starves the arm at ε/N forever.
          insertId: bannerInsertId(slotId, arm, device),
          source: SOURCE,
          description: `Banner principal — imagen ${device}`,
        })
      }
    }
  }

  return {
    elements_to_hide,
    elements_to_insert,
    elements_to_swap,
    template_config: {
      self_serve_banner: {
        // Legacy top-level fields = the DESKTOP control: banner-drift.js (B8)
        // fingerprints control_image and the results view (B7) reads the
        // marker's presence — both keep working unchanged.
        ...controlMarker(controls.desktop),
        devices: {
          desktop: controlMarker(controls.desktop),
          mobile: controlMarker(controls.mobile),
        },
      },
    },
  }
}
